import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Heart, Shield, MessageCircle, Sparkles, Users, BookOpen, Leaf, Mail, ArrowRight, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState(null);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !userType) {
      toast.error('Please enter your email and select who you are');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/waitlist/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, user_type: userType })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        setWaitlistPosition(data.position);
        toast.success('You\'re on the list!');
      } else {
        toast.error(data.detail || 'Something went wrong');
      }
    } catch (error) {
      toast.error('Failed to join. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Shield className="w-8 h-8" strokeWidth={1.5} />,
      title: "Privacy First",
      description: "Your identity stays protected until you choose to reveal it. Discreet, safe, and secure.",
      color: "bg-blue-50"
    },
    {
      icon: <Sparkles className="w-8 h-8" strokeWidth={1.5} />,
      title: "Compatibility Insights",
      description: "See detailed breakdowns of why you matched, with AI-powered conversation starters based on shared interests.",
      color: "bg-amber-50"
    },
    {
      icon: <MessageCircle className="w-8 h-8" strokeWidth={1.5} />,
      title: "Meaningful Chats",
      description: "Connect through thoughtful conversations with people who understand and appreciate you.",
      color: "bg-green-50"
    },
    {
      icon: <Users className="w-8 h-8" strokeWidth={1.5} />,
      title: "Compatibility Quizzes",
      description: "Discover deeper connections through our curated quizzes on love languages, values, and more.",
      color: "bg-rose-50"
    },
    {
      icon: <BookOpen className="w-8 h-8" strokeWidth={1.5} />,
      title: "Educational Content",
      description: "Learn from Kama Sutra wisdom to enhance intimacy and connection with your partner.",
      color: "bg-purple-50"
    },
    {
      icon: <Heart className="w-8 h-8" strokeWidth={1.5} />,
      title: "Non-Fetishistic",
      description: "A respectful space focused on genuine romantic connections, not objectification or fetishization.",
      color: "bg-orange-50"
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 gradient-mesh opacity-50" />
        
        <div className="container-app relative">
          {/* Navigation */}
          <nav className="flex items-center justify-between py-6" data-testid="landing-nav">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-2xl font-serif font-semibold">Twig</span>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="rounded-full"
                data-testid="login-btn"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/auth')}
                className="rounded-full bg-primary text-primary-foreground hover:opacity-90"
                data-testid="get-started-btn"
              >
                Get Started
              </Button>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
            {/* Left: Text */}
            <div className="animate-slideUp">
              <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                Dating for below-average size
              </p>
              <h1 className="text-5xl md:text-7xl font-serif font-semibold tracking-tight mb-6">
                Where size is{' '}
                <span className="text-gradient">never</span>{' '}
                the issue.
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                A non-fetishistic dating platform exclusively for cis and trans men with below-average 
                penile size (2-4.9"), connecting with women who genuinely prefer smaller partners. 
                Real connections, not objectification.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/auth')}
                  className="rounded-full bg-primary text-primary-foreground hover:opacity-90 px-8 py-6 text-lg btn-press"
                  data-testid="hero-cta-btn"
                >
                  Join Twig
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-full px-8 py-6 text-lg"
                >
                  Join Waitlist
                </Button>
              </div>
            </div>

            {/* Right: Visual */}
            <div className="relative animate-fadeIn hidden lg:block">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1743862182356-1a3722d35088?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" 
                  alt="Couple connection"
                  className="rounded-3xl shadow-float"
                />
              </div>
              {/* Floating cards */}
              <div className="absolute -top-4 -right-4 glass rounded-2xl p-4 shadow-soft animate-slideIn" style={{ animationDelay: '0.3s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Non-Fetishistic</p>
                    <p className="text-xs text-muted-foreground">Respect-focused dating</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 glass rounded-2xl p-4 shadow-soft animate-slideIn" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">2-4.9" Community</p>
                    <p className="text-xs text-muted-foreground">Exclusively for you</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Collection / Waitlist Section */}
      <section id="waitlist" className="section-spacing bg-primary/5 border-y border-primary/10">
        <div className="container-app">
          <div className="max-w-2xl mx-auto">
            {submitted ? (
              <div className="text-center animate-slideUp" data-testid="waitlist-success">
                <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={1.5} />
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-medium tracking-tight mb-4">
                  You're #{waitlistPosition} on the list!
                </h2>
                <p className="text-muted-foreground mb-6">
                  Thanks for joining the Twig waitlist. We'll notify you at <strong>{email}</strong> when we launch.
                </p>
                <div className="glass rounded-2xl p-6 text-left">
                  <p className="font-medium mb-3">As a founding member, you'll get:</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      Priority matching when we launch
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      First month FREE
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      Exclusive founding member badge
                    </li>
                  </ul>
                </div>
                <Button 
                  className="mt-6 rounded-full"
                  onClick={() => navigate('/auth')}
                >
                  Or Sign Up Now
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm uppercase tracking-widest text-primary mb-4">
                  Get Early Access
                </p>
                <h2 className="text-3xl md:text-4xl font-serif font-medium tracking-tight mb-4">
                  Join the Waitlist
                </h2>
                <p className="text-muted-foreground mb-8">
                  Be first to find your match. Early members get priority matching and their first month free.
                </p>

                <form onSubmit={handleWaitlistSubmit} className="space-y-4" data-testid="waitlist-form">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <Label htmlFor="waitlist-email" className="sr-only">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          id="waitlist-email"
                          type="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-12 h-14 rounded-2xl text-base"
                          data-testid="waitlist-email-input"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center gap-4">
                    <button
                      type="button"
                      onClick={() => setUserType('male')}
                      className={`flex-1 max-w-[180px] p-4 rounded-2xl border-2 transition-all ${
                        userType === 'male'
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 bg-background'
                      }`}
                      data-testid="waitlist-male-btn"
                    >
                      <span className="block text-2xl mb-1">👨</span>
                      <span className="text-sm font-medium">Man (2-4.9")</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserType('female')}
                      className={`flex-1 max-w-[180px] p-4 rounded-2xl border-2 transition-all ${
                        userType === 'female'
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 bg-background'
                      }`}
                      data-testid="waitlist-female-btn"
                    >
                      <span className="block text-2xl mb-1">👩</span>
                      <span className="text-sm font-medium">Woman</span>
                    </button>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !email || !userType}
                    size="lg"
                    className="w-full sm:w-auto rounded-full px-10 h-14 text-base btn-press"
                    data-testid="waitlist-submit-btn"
                  >
                    {loading ? 'Joining...' : (
                      <>
                        Join Waitlist
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-muted-foreground">
                    No spam, ever. Unsubscribe anytime.
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="section-spacing border-b border-border">
        <div className="container-app">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                Who is Twig for?
              </p>
              <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight mb-6">
                Clear about who we serve
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="card-organic">
                <h3 className="text-xl font-serif font-medium mb-3">For Men</h3>
                <p className="text-muted-foreground mb-4">
                  Twig is exclusively for <strong>cis and trans men with below-average penile size (2-4.9 inches)</strong>. 
                  If you're above this range, this isn't the right platform for you.
                </p>
                <p className="text-sm text-muted-foreground">
                  This is a space where your size is understood and appreciated, not judged.
                </p>
              </div>
              
              <div className="card-organic">
                <h3 className="text-xl font-serif font-medium mb-3">For Women</h3>
                <p className="text-muted-foreground mb-4">
                  Twig connects you with men who have smaller penises—whether your preference is 
                  <strong> personal, medical, or simply what feels right for you</strong>.
                </p>
                <p className="text-sm text-muted-foreground">
                  All male members are verified to be within the 2-4.9" range.
                </p>
              </div>
            </div>

            <div className="mt-8 p-6 rounded-2xl bg-primary/5 border border-primary/20 text-center">
              <p className="text-foreground mb-2">
                <strong>This is not a fetish site.</strong> Twig is built for genuine romantic connections 
                between adults seeking meaningful relationships—not objectification or kink.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>4 monthly payments, then FREE forever.</strong> A small commitment to ensure a quality community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section-spacing bg-accent/30">
        <div className="container-app">
          <div className="text-center mb-16 animate-slideUp">
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
              Why Twig?
            </p>
            <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight mb-4">
              Built for real connections
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We understand that finding love can be challenging. Twig creates a judgment-free, 
              non-fetishistic space where compatibility and character matter most.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children" data-testid="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`card-organic card-interactive hover-lift ${feature.color}`}
                data-testid={`feature-card-${index}`}
              >
                <div className="mb-4 text-primary">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-serif font-medium mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing">
        <div className="container-app">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
              Getting Started
            </p>
            <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight">
              How Twig works
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Create Your Profile", desc: "Sign up and complete your profile with your preferences and what you're looking for." },
              { step: "02", title: "Take Quizzes", desc: "Complete compatibility quizzes to help us find your best matches." },
              { step: "03", title: "Connect & Chat", desc: "Match with compatible people and start meaningful conversations." },
              { step: "04", title: "Enjoy Your Relationship", desc: "Experience a less anxious, more body positive, respect-led relationship." }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl font-serif font-light text-primary/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-serif font-medium mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-primary text-primary-foreground">
        <div className="container-app text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight mb-6">
            Ready to find your match?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Join our community of men (2-4.9") and women seeking genuine, respectful connections.
            No judgment, no fetishization—just real love.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              variant="secondary"
              onClick={() => navigate('/auth')}
              className="rounded-full px-10 py-6 text-lg btn-press"
              data-testid="cta-join-btn"
            >
              Join Twig Now
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' })}
              className="rounded-full px-10 py-6 text-lg border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
            >
              Or Join Waitlist
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                <Leaf className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-xl font-serif font-semibold">Twig</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 Twig. All rights reserved. Adults 18+ only. Non-fetishistic dating.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
